import React, { useEffect, useState } from 'react';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { Spinner } from '../components/ui/Spinner';
import { PAGE_ROUTE } from '../lib/routes';
import { getLastLoginMethod, setLastLoginMethod, getSession, setSession, LastLoginMethod, getPendingIntent, clearPendingIntent } from '../lib/utils';
import { Eye, EyeSlash, FacebookLogo, GoogleLogo } from '@phosphor-icons/react';
interface LoginProps {
  navigate: (page: string, context?: any) => void;
  context?: {
    verified?: boolean;
    passwordReset?: boolean;
    error?: string;
  };
}
function LastUsedBadge() {
  return <span className="absolute right-3 top-1/2 -translate-y-1/2 text-[10px] font-semibold uppercase tracking-wider bg-lime-1 text-dark-1 px-2 py-0.5 rounded-full" data-id="element-3612">
      Ultima dată
    </span>;
}
export function Login({
  navigate,
  context
}: LoginProps) {
  const isVerified = context?.verified === true;
  const isPasswordReset = context?.passwordReset === true;
  const verifyError = context?.error;
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [lastMethod, setLastMethod] = useState<LastLoginMethod | null>(null);
  useEffect(() => {
    setLastMethod(getLastLoginMethod());
  }, []);
  const persistSessionAndRoute = (loginMethod: LastLoginMethod) => {
    setLastLoginMethod(loginMethod);
    const existing = getSession();
    const role = existing?.role || 'demand';
    setSession({
      name: existing?.name || (email ? email.split('@')[0] : 'Utilizator'),
      email: email || existing?.email,
      phone: existing?.phone,
      avatar: existing?.avatar,
      role,
      hasListing: existing?.hasListing ?? true
    });
    // Consume any pending intent (auth gate from TenantAnunt etc.) and
    // bounce back to where the user came from with the right modal open.
    const intent = getPendingIntent();
    if (intent) {
      clearPendingIntent();
      navigate(PAGE_ROUTE.TENANT_ANUNT, {
        slug: intent.tenantSlug,
        openApplyModal: intent.type === 'send_offer'
      });
      return;
    }
    if (role === 'supply') {
      navigate(PAGE_ROUTE.MARKETPLACE);
    } else {
      navigate(PAGE_ROUTE.DASHBOARD_PROFIL);
    }
  };
  const handleCredentialsLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      await new Promise(resolve => setTimeout(resolve, 800));
      persistSessionAndRoute('email');
    } catch {
      setError('A apărut o eroare. Te rugăm să încerci din nou.');
    } finally {
      setLoading(false);
    }
  };
  const handleSocial = (method: 'facebook' | 'google') => {
    persistSessionAndRoute(method);
  };
  return <div className="w-full max-w-md mx-auto px-4 py-12" data-id="element-3613">
      <div className="text-center mb-8" data-id="element-3614">
        <h1 className="text-2xl font-bold text-light-1" data-id="element-3615">Bine ai revenit</h1>
        <p className="text-sm text-light-3 mt-2" data-id="element-3616">
          Nu ai cont?{' '}
          <button onClick={() => navigate(PAGE_ROUTE.REGISTER)} className="font-medium text-lime-1 hover:text-lime-2 cursor-pointer" data-id="element-3617">
            Creează unul
          </button>
        </p>
      </div>

      {isVerified && <div className="mb-4 p-3 rounded-lg bg-lime-5 border border-lime-4 text-lime-1 text-sm text-center" data-id="element-3618">
          Email confirmat! Te poți autentifica acum.
        </div>}

      {isPasswordReset && <div className="mb-4 p-3 rounded-lg bg-lime-5 border border-lime-4 text-lime-1 text-sm text-center" data-id="element-3619">
          Parola a fost actualizată. Te poți autentifica acum.
        </div>}

      {verifyError === 'invalid-token' && <div className="mb-4 p-3 rounded-lg bg-red-4 border border-red-2 text-red-1 text-sm text-center" data-id="element-3620">
          Link de verificare invalid sau deja folosit.
        </div>}

      {verifyError === 'token-expired' && <div className="mb-4 p-3 rounded-lg bg-dark-3 border border-dark-4 text-light-1 text-sm text-center" data-id="element-3621">
          Link-ul de verificare a expirat. Te rugăm să creezi un cont din nou.
        </div>}

      <div className="space-y-4" data-id="element-3622">
        <div className="relative" data-id="element-3623">
          <Button onClick={() => handleSocial('facebook')} className="w-full h-12 bg-[#1877F2]/10 hover:bg-[#1877F2]/20 text-light-1 border border-[#1877F2]/30 relative" data-id="element-3624">
            <FacebookLogo weight="fill" className="w-5 h-5 mr-3 text-[#1877F2]" data-id="element-3625" />
            Continuă cu Facebook
          </Button>
          {lastMethod === 'facebook' && <LastUsedBadge data-id="element-3626" />}
        </div>

        <div className="relative" data-id="element-3627">
          <Button onClick={() => handleSocial('google')} className="w-full h-12 bg-dark-2 hover:bg-dark-3 text-light-1 border border-dark-3 relative" data-id="element-3628">
            <GoogleLogo weight="bold" className="w-5 h-5 mr-3" data-id="element-3629" />
            Continuă cu Google
          </Button>
          {lastMethod === 'google' && <LastUsedBadge data-id="element-3630" />}
        </div>

        <div className="relative py-4" data-id="element-3631">
          <div className="absolute inset-0 flex items-center" data-id="element-3632">
            <span className="w-full border-t border-dark-3" data-id="element-3633" />
          </div>
          <div className="relative flex justify-center text-xs uppercase" data-id="element-3634">
            <span className="bg-dark-1 px-2 text-light-3" data-id="element-3635">sau cu email</span>
          </div>
        </div>

        <form onSubmit={handleCredentialsLogin} className="space-y-4 relative" data-id="element-3636">
          {lastMethod === 'email' && <div className="absolute -top-2 right-0 z-10" data-id="element-3637">
              <span className="text-[10px] font-semibold uppercase tracking-wider bg-lime-1 text-dark-1 px-2 py-0.5 rounded-full" data-id="element-3638">
                Ultima dată
              </span>
            </div>}

          {error && <div className="p-3 rounded-lg bg-red-4 border border-red-2 text-red-1 text-sm" data-id="element-3639">
              {error}
            </div>}

          <Input type="email" placeholder="Adresa de email" value={email} onChange={e => setEmail(e.target.value)} required autoComplete="email" className="h-12 bg-dark-2 border-dark-3" data-id="element-3640" />

          <div className="relative" data-id="element-3641">
            <Input type={showPassword ? 'text' : 'password'} placeholder="Parola" value={password} onChange={e => setPassword(e.target.value)} required autoComplete="current-password" className="h-12 bg-dark-2 border-dark-3 pr-12" data-id="element-3642" />
            <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-light-3 hover:text-light-1 p-1" data-id="element-3643">
              {showPassword ? <EyeSlash className="w-5 h-5" data-id="element-3644" /> : <Eye className="w-5 h-5" data-id="element-3645" />}
            </button>
          </div>

          <div className="flex justify-end" data-id="element-3646">
            <button type="button" onClick={() => navigate(PAGE_ROUTE.RESET_PASSWORD)} className="text-xs text-lime-1 hover:text-lime-2" data-id="element-3647">
              Ai uitat parola?
            </button>
          </div>

          <Button type="submit" disabled={loading} className="w-full h-12 bg-lime-1 text-dark-1 hover:bg-lime-2 font-semibold text-base" data-id="element-3648">
            {loading ? <span className="flex items-center gap-2" data-id="element-3649">
                <Spinner size="sm" data-id="element-3650" />
                Se autentifică...
              </span> : 'Autentificare'}
          </Button>
        </form>
      </div>

      <p className="text-xs text-light-3 text-center leading-relaxed mt-8" data-id="element-3651">
        Continuând, confirmi că ai citit și ești de acord cu{' '}
        <button onClick={() => navigate(PAGE_ROUTE.TERMENI)} className="text-light-2 underline hover:text-lime-1" data-id="element-3652">
          Termenii și Condițiile
        </button>{' '}
        și{' '}
        <button onClick={() => navigate(PAGE_ROUTE.CONFIDENTIALITATE)} className="text-light-2 underline hover:text-lime-1" data-id="element-3653">
          Politica de Confidențialitate
        </button>
        .
      </p>
    </div>;
}